import { PayloadAction, createSlice, current } from "@reduxjs/toolkit";
import { IAppEntity } from "../../config/entities";
import { debug } from "console";

export interface ITab {
  id: string;
  title: string;
  side: "left" | "right";
  component: any;
  isModified: boolean;
  entity?: IAppEntity;
  documentNumber?: string;
  documentDate?: any;
  documentStatus?: number;
  parentTabId?: string;
}

interface ITabsState {
  tabs: ITab[];
  previousTabs: string[];
  activeTab?: string;
  renderType: "vertical" | "horizontal";
}

const initialState: ITabsState = {
  activeTab: undefined,
  previousTabs: [],
  renderType: "horizontal",
  tabs: [],
};

const newTabSlice = createSlice({
  name: "newTab",
  initialState,
  reducers: {
    addTab: (state, action: PayloadAction<ITab>) => {
      const newTab = action.payload;
      if (state.activeTab && state.activeTab !== newTab.id) {
        state.previousTabs.push(state.activeTab);
      }

      if (state.activeTab) {
        const currentTab = state.tabs.find((tab) => tab.id === state.activeTab);

        if (currentTab) {
          newTab.side = currentTab.side;
        }
      }
      state.tabs.push(newTab);
      state.activeTab = newTab.id;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      if (state.activeTab && state.activeTab !== action.payload) {
        state.previousTabs.push(state.activeTab);
      }
      state.activeTab = action.payload;
    },
    // this became a bit complex so here are comments of what does what
    removeTab: (state, action: PayloadAction<string>) => {
      // here I find which tab to remove
      const tabToRemove = state.tabs.find((tab) => tab.id === action.payload);

      // and removing it from tabs
      state.tabs = state.tabs.filter((tab) => tab.id !== action.payload);

      // because it was pushed in previousTabs, removing it from here as well
      state.previousTabs = state.previousTabs.filter(
        (tabId) => tabId !== action.payload
      );

      // setting active tab after removing based of having parent tab or not
      if (tabToRemove && tabToRemove.parentTabId) {
        state.activeTab = tabToRemove.parentTabId;
      } else if (state.activeTab === action.payload) {
        if (state.previousTabs.length > 0) {
          state.activeTab = state.previousTabs.pop();
        } else {
          const sameSideTabs = state.tabs.filter(
            (tab) => tab.side === tabToRemove?.side && tab.id !== action.payload
          );

          if (sameSideTabs.length > 0) {
            state.activeTab = sameSideTabs[sameSideTabs.length - 1].id;
          } else if (state.tabs.length > 1) {
            state.activeTab = state.tabs.find(
              (tab) => tab.id !== action.payload
            )?.id;
          } else {
            state.activeTab = undefined;
          }
        }
      }
    },
    setIsTabModified: (
      state,
      action: PayloadAction<{ tabId: string; isChanged: boolean }>
    ) => {
      const tab = state.tabs.find((x) => x.id === action.payload.tabId);
      if (tab) {
        if (action.payload.isChanged) {
          if (!tab.title.endsWith("*")) {
            tab.title += "*";
          }
        } else {
          tab.title = (tab.title || "").replace(/\s?\*/, "");
        }
      }
    },
    moveTab: (
      state,
      action: PayloadAction<{
        tabId: string;
        overTabId: string;
        direction: "left" | "right";
      }>
    ) => {
      const { tabId, overTabId, direction } = action.payload;

      const movingTabIndex = state.tabs.findIndex((x) => x.id === tabId);
      const movingTab = state.tabs[movingTabIndex];

      const overTabIndex = state.tabs.findIndex((x) => x.id === overTabId);

      if (movingTabIndex !== -1 && overTabIndex !== -1) {
        state.tabs.splice(movingTabIndex, 1);
        let adjustedTargetIndex;
        if (direction === "left") {
          adjustedTargetIndex =
            movingTabIndex < overTabIndex ? overTabIndex : overTabIndex;
        } else {
          adjustedTargetIndex =
            movingTabIndex < overTabIndex ? overTabIndex + 1 : overTabIndex + 1;
        }

        state.tabs.splice(adjustedTargetIndex, 0, movingTab);

        const overTab = state.tabs[overTabIndex];
        movingTab.side = overTab.side;
      }
    },

    setRenderType: (
      state,
      action: PayloadAction<"vertical" | "horizontal">
    ) => {
      state.renderType = action.payload;
    },
    setSide: (
      state,
      action: PayloadAction<{ tabId: string; side: "left" | "right" }>
    ) => {
      const tab = state.tabs.find((x) => x.id === action.payload.tabId);
      if (tab) {
        const defaultSide = tab.side;
        tab.side = action.payload.side;

        const defaultSideTabs = state.tabs.filter(
          (t) => t.side === defaultSide && t.id !== action.payload.tabId
        );

        if (defaultSideTabs.length > 0) {
          state.activeTab = defaultSideTabs[defaultSideTabs.length - 1].id;
        }
        // if (
        //   defaultSide !== action.payload.side &&
        //   action.payload.side === "right"
        // ) {
        //   state.activeTab = action.payload.tabId;
        // }
      }
    },
  },
});

export const {
  addTab,
  removeTab,
  moveTab,
  setIsTabModified,
  setActiveTab,
  setRenderType,
  setSide,
} = newTabSlice.actions;
export const newTab = newTabSlice.reducer;
